import React, { useMemo } from 'react'
import { Package } from 'lucide-react'
import Card from '../Card'
import DataTable, { Column } from '../DataTable'

interface LowStockProduct {
  id: string
  name: string
  image?: string
  currentStock: number
  lowStockThreshold: number
}

interface LowStockProductsTableProps {
  products: LowStockProduct[]
  loading?: boolean
}

export default function LowStockProductsTable({ products, loading = false }: LowStockProductsTableProps) {
  const getStockStatus = (current: number, threshold: number) => {
    if (current === 0) return { status: 'Out of Stock', color: 'bg-red-100 text-red-800' }
    if (current <= threshold / 2) return { status: 'Critical', color: 'bg-red-100 text-red-800' }
    if (current <= threshold) return { status: 'Low', color: 'bg-yellow-100 text-yellow-800' }
    return { status: 'Normal', color: 'bg-green-100 text-green-800' }
  }

  const columns: Column<LowStockProduct>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Product',
        render: (value, row) => (
          <div className="flex items-center gap-3">
            {row.image ? (
              <img src={row.image} alt={value} className="w-10 h-10 rounded-lg object-cover" />
            ) : (
              <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                <Package size={16} className="text-gray-400" />
              </div>
            )}
            <span>{value}</span>
          </div>
        ),
        sortable: true,
        sortType: 'text',
      },
      {
        key: 'lowStockThreshold',
        header: 'Threshold',
        sortable: true,
        sortType: 'number',
      },
      {
        key: 'currentStock',
        header: 'Stock',
        render: (value, row) => {
          const stockInfo = getStockStatus(value, row.lowStockThreshold)
          return (
            <div className="flex items-center gap-3">
              <span className={`px-2 py-1 rounded text-xs font-medium ${stockInfo.color}`}>
                {stockInfo.status}
              </span>
              <span className="font-semibold">{value} units</span>
            </div>
          )
        },
        sortable: true,
        sortType: 'number',
      },
    ],
    []
  )

  if (loading) {
    return (
      <Card title="Low Stock Products" subtitle="Products below configured threshold">
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-lg"></div>
          ))}
        </div>
      </Card>
    )
  }

  if (!products || products.length === 0) {
    return (
      <Card title="Low Stock Products" subtitle="Products below configured threshold">
        <div className="py-12 flex flex-col items-center justify-center text-gray-500">
          <Package size={40} className="mb-2 opacity-50" />
          <p>All products are well stocked!</p>
        </div>
      </Card>
    )
  }

  return (
    <Card title="Low Stock Products" subtitle="Products below configured threshold">
      <DataTable<LowStockProduct>
        columns={columns}
        data={products}
      />
    </Card>
  )
}
